import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Drawer,
  Input,
  Space,
  Badge,
  Avatar,
  Dropdown,
  AutoComplete,
  Select,
} from 'antd';
import {
  MenuOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  HeartOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  FileTextOutlined,
  GiftOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  authService,
  cartService,
  wishlistService,
  productService,
  currencyService,
} from '../services/api';
import WhatsAppButton from './WhatsAppButton';
import OffersBanner from './OffersBanner';
import BrandIcon from './BrandIcon';
import { siFacebook, siInstagram, siWhatsapp, siYoutube, siVisa, siMastercard } from 'simple-icons';

const { Header, Footer, Content } = Layout;

const PublicLayout = () => {
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Currency state
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  useEffect(() => {
    currencyService
      .getCurrencies()
      .then((res) => {
        const list = res.data || [];
        setCurrencies(list);
        const def = list.find((c) => c.is_default);
        setSelectedCurrency(def?.code || 'NPR');
      })
      .catch(() => {});
  }, []);

  // Footer defaults (Nepal market)
  const footerDefaults = {
    tagline:
      'Your trusted partner for electrical appliances, electronics, and expert repair services across Nepal.',
    phone: '+977 980-544-6865',
    email: 'info@currentcarenpl.com',
    copyright_text: `© ${new Date().getFullYear()} Current Care. All rights reserved.`,
    products_heading: 'Products',
    services_heading: 'Services',
    support_heading: 'Support',
  };

  useEffect(() => {
    checkAuthStatus();

    // Listen for storage changes to update auth state across tabs
    const handleStorageChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCartCount();
      fetchWishlistCount();
    }
  }, [isAuthenticated]);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    const isAuthenticated = !!token;
    setIsAuthenticated(isAuthenticated);
    if (isAuthenticated) {
      try {
        setUser(JSON.parse(localStorage.getItem('authUser') || 'null'));
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await cartService.getCart();
      const items = response.data?.items || [];
      setCartCount(items.length);
    } catch {
      setCartCount(0);
    }
  };

  const fetchWishlistCount = async () => {
    try {
      const response = await wishlistService.getWishlist();
      const items = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setWishlistCount(items.length);
    } catch {
      setWishlistCount(0);
    }
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Products' },
    { to: '/services', label: 'Services' },
    { to: '/service-catalog', label: 'Repair & Fixes' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/testimonials', label: 'Testimonials' },
    { to: '/contact', label: 'Contact' },
    { to: '/blog', label: 'Blog' },
    { to: '/about', label: 'About' },
  ];

  const menuItems = [
    { key: '/', label: <Link to="/">Home</Link> },
    { key: '/products', label: <Link to="/products">Products</Link> },
    { key: '/services', label: <Link to="/services">Services</Link> },
    { key: '/service-catalog', label: <Link to="/service-catalog">Repair & Fixes</Link> },
    { key: '/how-it-works', label: <Link to="/how-it-works">How It Works</Link> },
    { key: '/testimonials', label: <Link to="/testimonials">Testimonials</Link> },
    { key: '/contact', label: <Link to="/contact">Contact</Link> },
    { key: '/blog', label: <Link to="/blog">Blog</Link> },
    { key: '/about', label: <Link to="/about">About</Link> },
  ];

  const userMenuItems = [
    { key: 'cart', icon: <ShoppingCartOutlined />, label: 'Shopping Cart' },
    { key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
    { key: 'orders', icon: <FileTextOutlined />, label: 'My Orders' },
    { key: 'wishlist', icon: <HeartOutlined />, label: 'Wishlist' },
    { key: 'notifications', icon: <BellOutlined />, label: 'Notifications' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout' },
  ];

  const onSearch = (value) => {
    if (!value) return;
    navigate(`/products?search=${encodeURIComponent(value)}`);
  };

  const onSearchChange = (value) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) {
      setSearchOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await productService.searchProducts(value);
        const data = response.data?.data || response.data || [];
        const items = Array.isArray(data) ? data.slice(0, 8) : [];
        setSearchOptions(
          items.map((p) => ({
            value: p.name,
            label: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>{p.name}</span>
                <span style={{ color: '#0363d9', fontWeight: 600, fontSize: 12 }}>
                  NPR {Number((p.sale_price ?? p.price) || 0).toLocaleString()}
                </span>
              </div>
            ),
            id: p.id,
          })),
        );
      } catch {
        setSearchOptions([]);
      }
    }, 300);
  };

  const onSearchSelect = (value, option) => {
    if (option?.id) {
      navigate(`/products/${option.id}`);
    } else if (value) {
      navigate(`/products?search=${encodeURIComponent(value)}`);
    }
    setSearchValue('');
    setSearchOptions([]);
  };

  const handleUserMenuClick = ({ key }) => {
    switch (key) {
      case 'profile':
        if (isAuthenticated) navigate('/profile');
        break;
      case 'orders':
        if (isAuthenticated) navigate('/orders');
        break;
      case 'wishlist':
        if (isAuthenticated) navigate('/wishlist');
        break;
      case 'settings':
        if (isAuthenticated) navigate('/settings');
        break;
      case 'notifications':
        if (isAuthenticated) navigate('/notifications');
        break;
      case 'cart':
        navigate('/cart');
        break;
      case 'logout':
        // Handle logout
        authService.logout();
        break;
      default:
        break;
    }
  };

  return (
    <Layout className="min-h-screen">
      {/* Promotional offers banner, rotates latest offers, links to /offers */}
      <OffersBanner />

      <Header
        style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 1px 0 rgba(15,23,42,0.08)',
          padding: '0 16px',
          position: 'sticky',
          top: 0,
          zIndex: 30,
          height: 64,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 0',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Link
              to="/"
              className="flex items-center gap-2 no-underline"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <img
                src="/images/currentcare-logo.png"
                alt="Current Care"
                style={{ height: 40, width: 'auto' }}
                onError={(e) => {
                  e.target.src =
                    'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232563eb%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
                }}
              />
              <span className="hidden text-[22px] font-extrabold leading-none tracking-tight sm:inline">
                <span style={{ color: '#0f172a' }}>Current</span>
                <span style={{ color: '#0363d9' }}>Care</span>
              </span>
            </Link>
          </div>

          {/* Search with AutoComplete */}
          <div
            style={{ flex: 1, minWidth: 0, maxWidth: 460, margin: '0 12px' }}
            className="search-container"
          >
            <AutoComplete
              value={searchValue}
              options={searchOptions}
              onSelect={onSearchSelect}
              onSearch={onSearchChange}
              style={{ width: '100%' }}
              notFoundContent={searchValue.length >= 2 ? 'No products found' : null}
            >
              <Input
                placeholder="Search products, brands & services…"
                size="large"
                allowClear
                prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: 16 }} />}
                onPressEnter={() => onSearch(searchValue)}
                style={{ borderRadius: 999, background: '#f1f5f9' }}
              />
            </AutoComplete>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Currency Selector */}
            {currencies.length > 1 && (
              <div style={{ minWidth: 80 }}>
                <Select
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  style={{ width: '100%' }}
                  size="small"
                  className="hidden md:inline-flex"
                  options={currencies
                    .filter((c) => c.is_active)
                    .map((c) => ({
                      value: c.code,
                      label: `${c.symbol} ${c.code}`,
                    }))}
                />
              </div>
            )}

            {/* Cart, always visible */}
            <Badge
              count={isAuthenticated ? cartCount : 0}
              showZero={false}
              size="small"
              offset={[-3, 3]}
            >
              <Button
                type="primary"
                size="middle"
                icon={<ShoppingCartOutlined style={{ fontSize: 18 }} />}
                onClick={() => navigate('/cart')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
              >
                <span className="hidden sm:inline">Cart</span>
              </Button>
            </Badge>

            {/* Wishlist, only for logged-in users */}
            {isAuthenticated && (
              <Badge count={wishlistCount} showZero={false} size="small" offset={[-3, 3]}>
                <Button
                  size="middle"
                  icon={<HeartOutlined style={{ fontSize: 18 }} />}
                  onClick={() => navigate('/wishlist')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: '#fff',
                    color: '#0f172a',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    fontWeight: 600,
                  }}
                >
                  <span className="hidden sm:inline">Wishlist</span>
                </Button>
              </Badge>
            )}

            {isAuthenticated ? (
              <Dropdown
                menu={{
                  items: userMenuItems.filter((item) => item.key !== 'cart'),
                  onClick: handleUserMenuClick,
                }}
                placement="bottomRight"
                arrow
              >
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center"
                  style={{
                    height: 40,
                    padding: '0 14px 0 4px',
                    gap: 8,
                    borderRadius: 999,
                    background: 'linear-gradient(135deg, #2d5a3d, #3d7a4d)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(45,90,61,0.25)',
                  }}
                >
                  <Avatar
                    size={30}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#a8e6cf', color: '#2d5a3d', flexShrink: 0 }}
                  />
                  <span
                    className="hidden sm:inline"
                    style={{
                      color: '#ffffff',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user?.name || user?.full_name || 'Account'}
                  </span>
                </div>
              </Dropdown>
            ) : (
              <div className="flex gap-1 sm:gap-2">
                <Button
                  ghost
                  type="primary"
                  size="middle"
                  icon={<UserOutlined className="sm:hidden" />}
                  onClick={() => navigate('/login')}
                  style={{ fontWeight: 600 }}
                >
                  <span className="hidden sm:inline">Login</span>
                </Button>
              </div>
            )}
          </div>

          <Button
            className="hamburger-btn"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
          />
        </div>
      </Header>

      {/* Desktop category navigation */}
      <nav className="hidden border-b border-slate-200 bg-white lg:block">
        <div className="max-w-content mx-auto flex items-center gap-1 px-4">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`relative px-3 py-3 text-sm font-medium transition-colors ${
                  active
                    ? 'text-brand-600'
                    : l.accent
                      ? 'text-[#fe9c07] hover:text-[#e08c00]'
                      : 'text-slate-600 hover:text-brand-600'
                }`}
              >
                {l.accent && <ThunderboltOutlined className="mr-1" />}
                {l.label}
                {active && (
                  <span className="bg-brand-600 absolute inset-x-3 bottom-0 h-0.5 rounded-full" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
      >
        <div className="mb-4">
          <Input.Search
            placeholder="Search products..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={onSearch}
            enterButton
          />
        </div>

        <div className="mb-4 flex gap-2">
          <Button
            icon={<HeartOutlined />}
            block
            onClick={() => (isAuthenticated ? navigate('/wishlist') : navigate('/login'))}
          >
            Wishlist ({isAuthenticated ? wishlistCount : 0})
          </Button>
          <Button icon={<ShoppingCartOutlined />} block onClick={() => navigate('/cart')}>
            Cart ({isAuthenticated ? cartCount : 0})
          </Button>
        </div>

        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems.map((item) => ({ key: item.key, label: item.label }))}
          onClick={() => setMobileMenuVisible(false)}
        />

        <Space direction="vertical" style={{ width: '100%', marginTop: 24 }}>
          {isAuthenticated ? (
            <>
              <Button
                icon={<UserOutlined />}
                block
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuVisible(false);
                }}
              >
                My Profile
              </Button>
              <Button
                icon={<ShoppingCartOutlined />}
                block
                onClick={() => {
                  navigate('/orders');
                  setMobileMenuVisible(false);
                }}
              >
                My Orders
              </Button>
              <Button
                icon={<HeartOutlined />}
                block
                onClick={() => {
                  navigate('/wishlist');
                  setMobileMenuVisible(false);
                }}
              >
                Wishlist
              </Button>
              <Button
                icon={<GiftOutlined />}
                block
                onClick={() => {
                  navigate('/gift-certificates');
                  setMobileMenuVisible(false);
                }}
              >
                Gift Certificates
              </Button>
              <Button
                icon={<ThunderboltOutlined />}
                block
                onClick={() => {
                  navigate('/affiliate');
                  setMobileMenuVisible(false);
                }}
              >
                Affiliate
              </Button>
              <Button
                icon={<SettingOutlined />}
                block
                onClick={() => {
                  navigate('/settings');
                  setMobileMenuVisible(false);
                }}
              >
                Settings
              </Button>
              <Button danger block onClick={() => authService.logout()}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                block
                onClick={() => {
                  navigate('/login');
                  setMobileMenuVisible(false);
                }}
              >
                Login
              </Button>
            </>
          )}
        </Space>
      </Drawer>

      <Content className="flex-1">
        <Outlet />
      </Content>

      <Footer style={{ background: '#08152b', padding: 0 }}>
        <div className="max-w-content mx-auto px-4 py-14">
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-6">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-2">
              <img
                src="/images/currentcare-logo.png"
                alt="Current Care"
                className="mb-4 h-12 w-auto"
              />
              <p className="max-w-xs text-sm leading-relaxed text-slate-400">
                {footerDefaults.tagline}
              </p>
              <div className="mt-5 flex gap-2.5">
                {[
                  { icon: siWhatsapp, href: 'https://wa.me/9779805446865' },
                  { icon: siFacebook, href: 'https://facebook.com' },
                  { icon: siInstagram, href: 'https://instagram.com' },
                  { icon: siYoutube, href: 'https://youtube.com' },
                ].map((s) => (
                  <a
                    key={s.icon.title}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.icon.title}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-brand-600"
                  >
                    <BrandIcon icon={s.icon} size={16} color="#ffffff" />
                  </a>
                ))}
              </div>
            </div>

            {/* Shop */}
            <div>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-white">
                Shop
              </h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/products?category=appliances" className="footer-link">
                    Appliances
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=electronics" className="footer-link">
                    Electronics
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=components" className="footer-link">
                    Components
                  </Link>
                </li>
                <li>
                  <Link to="/offers" className="footer-link">
                    Special Offers
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-white">
                Company
              </h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/about" className="footer-link">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/service-catalog" className="footer-link">
                    Book a Service
                  </Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="footer-link">
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="footer-link">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="col-span-2">
              <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-white">
                Get in touch
              </h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-start gap-2.5">
                  <EnvironmentOutlined style={{ color: '#0799f7', marginTop: 3 }} />
                  <span>New Road, Kathmandu, Nepal</span>
                </li>
                <li>
                  <a
                    href={`tel:${footerDefaults.phone}`}
                    className="footer-link flex items-center gap-2.5"
                  >
                    <PhoneOutlined style={{ color: '#0799f7' }} />
                    {footerDefaults.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${footerDefaults.email}`}
                    className="footer-link flex items-center gap-2.5"
                  >
                    <MailOutlined style={{ color: '#0799f7' }} />
                    {footerDefaults.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Payments + copyright */}
          <div className="mt-12 flex flex-col gap-5 border-t border-white/10 pt-6 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-slate-400">
              {footerDefaults.copyright_text}
              <span className="mx-2 text-slate-600">·</span>
              <Link to="/privacy" className="footer-link">
                Privacy
              </Link>
              <span className="mx-2 text-slate-600">·</span>
              <Link to="/terms" className="footer-link">
                Terms
              </Link>
            </p>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wide text-slate-500">We accept</span>
              <span className="rounded-md bg-[#60bb46] px-2 py-1 text-xs font-bold text-white">
                eSewa
              </span>
              <span className="rounded-md bg-[#5c2d91] px-2 py-1 text-xs font-bold text-white">
                Khalti
              </span>
              <span className="flex h-7 w-11 items-center justify-center rounded-md bg-white">
                <BrandIcon icon={siVisa} size={26} />
              </span>
              <span className="flex h-7 w-11 items-center justify-center rounded-md bg-white">
                <BrandIcon icon={siMastercard} size={24} />
              </span>
            </div>
          </div>
        </div>
      </Footer>
      <WhatsAppButton />
    </Layout>
  );
};

export default PublicLayout;
